/**
 * Scannable entity types — what can be encoded into a QR code and
 * resolved by the backend `/scan/:entityType/:entityId` endpoint.
 * Mirrors the Phase 5c backend tuple in `labels.types.ts`.
 *
 *   part_serial      — a specific unit of a serialized part
 *   equipment        — a piece of equipment on a vessel
 *   certificate      — a regulatory compliance certificate
 *   drydock_campaign — a planned shipyard operation
 *   work_order       — a WO (typically scanned from the paper printout)
 */
export const SCANNABLE_ENTITY_TYPES = [
  'part_serial',
  'equipment',
  'certificate',
  'drydock_campaign',
  'work_order',
] as const;

export type ScannableEntityType = (typeof SCANNABLE_ENTITY_TYPES)[number];

export function isScannableEntityType(v: string): v is ScannableEntityType {
  return (SCANNABLE_ENTITY_TYPES as readonly string[]).includes(v);
}

/**
 * Summary returned by `/scan/:entityType/:entityId`. The frontend uses
 * `entityType` to route to the correct detail page and `title +
 * subtitle` for the on-camera confirmation banner.
 *
 * `vesselId` and `companyId` are surfaced so the client can refresh
 * its scope filters after scanning (e.g. switch the vessel selector
 * to the vessel that owns the scanned equipment).
 */
export interface ScannedEntitySummary {
  entityType: ScannableEntityType;
  entityId: string;
  title: string;
  subtitle?: string;
  vesselId?: string | null;
  companyId: string;
}

/**
 * Thermal label formats supported in Phase 5c. Dimensions are the
 * printable area in millimetres.
 */
export const LABEL_FORMATS = ['dymo32x57', 'brother62x29'] as const;
export type LabelFormat = (typeof LABEL_FORMATS)[number];

export interface LabelDimensionsMm {
  widthMm: number;
  heightMm: number;
}

export const LABEL_DIMENSIONS: Record<LabelFormat, LabelDimensionsMm> = {
  // DYMO LabelWriter 450 — small multipurpose label (ref 11354)
  dymo32x57: { widthMm: 57, heightMm: 32 },
  // Brother QL-820NWB — die-cut address label (ref DK-11209)
  brother62x29: { widthMm: 62, heightMm: 29 },
};

/**
 * Map a scanned entity to its detail-page route. The router is the
 * single source of truth for paths; this helper centralizes the
 * mapping so a callsite (ScanPage redirect, label preview "Open"
 * button) doesn't have to switch on entityType inline.
 *
 * `part_serial` routes to its parent part detail with the serial id
 * as a query param — the part-serial subpage is rendered inline on
 * the part detail (Phase 4).
 */
export function entityRoutePath(
  entityType: ScannableEntityType,
  entityId: string,
): string {
  switch (entityType) {
    case 'equipment':
      return `/equipment/${entityId}`;
    case 'certificate':
      return `/certificates/${entityId}`;
    case 'drydock_campaign':
      return `/drydock-campaigns/${entityId}`;
    case 'work_order':
      return `/work-orders/${entityId}`;
    case 'part_serial':
      // The part-serial UI lives under the parent part detail page;
      // backend resolver returns the parent part's id in the
      // `subtitle`/`vesselId` envelope, but for a consistent route we
      // still link by the serial id and let the part detail page
      // surface the matching unit.
      return `/parts?serialId=${entityId}`;
  }
}
